import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import AppLayout from '../components/AppLayout';

const InterventionsOnlyView = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planData, setPlanData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get data from navigation state
        if (location.state) {
          setPlanData(location.state.planData);
        }
        
        // Fetch student data
        const studentRef = doc(db, 'students', id);
        const studentSnap = await getDoc(studentRef);
        
        if (studentSnap.exists()) {
          setStudent({ id: studentSnap.id, ...studentSnap.data() });
        } else {
          navigate('/students');
          return;
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, location.state, navigate]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </AppLayout>
    );
  }

  if (!student) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Student Not Found</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>The student could not be found in the database.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Interventions Plan
              </h1>
              <p className="text-gray-600 mt-1">
                {student.firstName} {student.lastName} - {student.yearGroup}
              </p>
              <p className="text-sm text-blue-600 mt-1">
                Primary school student at/near National Curriculum level
              </p>
            </div>
            <button
              onClick={() => navigate(`/student/${id}`)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back to Student Profile
            </button>
          </div>
        </div>

        {/* Notice */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Interventions-Only Path
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  This student is working at or near National Curriculum level. 
                  They will access the standard curriculum with targeted interventions and support strategies.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interventions Content */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          {planData ? (
            <div className="space-y-6">
              {/* Child Profile Echo */}
              {planData.childProfileEcho && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">Student Profile</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p><strong>Name:</strong> {planData.childProfileEcho.name}</p>
                      <p><strong>Year Group:</strong> {planData.childProfileEcho.yearGroup}</p>
                      <p><strong>NC Status:</strong> {planData.childProfileEcho.ncStatus}</p>
                    </div>
                    <div>
                      {planData.childProfileEcho.focusTags && (
                        <div>
                          <p><strong>Focus Areas:</strong></p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {planData.childProfileEcho.focusTags.map((tag, index) => (
                              <span 
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Recommended Interventions */}
              {planData.recommendedInterventions && planData.recommendedInterventions.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Recommended Interventions</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {planData.recommendedInterventions.map((intervention, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{intervention.title}</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {intervention.type} • {intervention.recommendedFrequency}
                            </p>
                            {intervention.slotMins && (
                              <p className="text-sm text-gray-500 mt-1">
                                Duration: {intervention.slotMins} minutes
                              </p>
                            )}
                          </div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {intervention.type}
                          </span>
                        </div>
                        
                        {intervention.focusTags && intervention.focusTags.length > 0 && (
                          <div className="mt-3">
                            <div className="flex flex-wrap gap-1">
                              {intervention.focusTags.map((tag, tagIndex) => (
                                <span 
                                  key={tagIndex}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {intervention.notesForStaff && intervention.notesForStaff.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700">Staff Notes:</p>
                            <ul className="text-sm text-gray-600 list-disc list-inside mt-1">
                              {intervention.notesForStaff.map((note, noteIndex) => (
                                <li key={noteIndex}>{note}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Existing Group Matches */}
              {planData.existingGroupMatches && planData.existingGroupMatches.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Existing Group Matches</h2>
                  <div className="space-y-4">
                    {planData.existingGroupMatches.map((match, index) => (
                      <div key={index} className="border border-green-200 rounded-lg p-4 bg-green-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">Group Match Found</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Match Score: {Math.round(match.matchScore * 100)}%
                            </p>
                          </div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {Math.round(match.matchScore * 100)}% Match
                          </span>
                        </div>
                        
                        {match.reasons && match.reasons.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700">Why this match:</p>
                            <ul className="text-sm text-gray-600 list-disc list-inside mt-1">
                              {match.reasons.map((reason, reasonIndex) => (
                                <li key={reasonIndex}>{reason}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Group Suggestions */}
              {planData.newGroupSuggestions && planData.newGroupSuggestions.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">New Group Suggestions</h2>
                  <div className="space-y-4">
                    {planData.newGroupSuggestions.map((suggestion, index) => (
                      <div key={index} className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                        <h3 className="font-medium text-gray-900">{suggestion.suggestedTitle}</h3>
                        <p className="text-sm text-gray-600 mt-1">{suggestion.rationale}</p>
                        
                        {suggestion.suggestedSchedule && suggestion.suggestedSchedule.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700">Suggested Schedule:</p>
                            <div className="mt-1">
                              {suggestion.suggestedSchedule.map((schedule, scheduleIndex) => (
                                <span 
                                  key={scheduleIndex}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mr-2 mb-1"
                                >
                                  {schedule.day} at {schedule.time}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {suggestion.suggestedCapacity && (
                          <p className="text-sm text-gray-600 mt-2">
                            <strong>Suggested Capacity:</strong> {suggestion.suggestedCapacity} students
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No intervention data available</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default InterventionsOnlyView;
